"use client";

import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentDoctor } from "../../authDoctor/redux/thunks/doctorsThunks";
import {
  createPrescription,
  classifyAndSavePrescriptionDocuments,
  fetchPrescriptionsByPatientForDoctor,
  fetchPrescriptionByConsultation,
  updatePrescription,
} from "../redux/thunks/prescriptionThunks";

const pick = (v, fallback) => {
  const x = typeof v === "string" ? v.trim() : v;
  return x ? x : fallback;
};

// Maps the document tab key to the backend's required TypeOrdonnance enum value.
const DOCUMENT_TYPE_TO_ORDONNANCE = {
  prescription: "ordonnance",
  radio: "radio_imagerie",
  bilan: "bilan_test_biologique",
  lettre: "autre",
};

const normalizeId = (v) => {
  if (v == null) return null;

  if (typeof v === "object" && v !== null) {
    const id =
      v.id ||
      v.userId ||
      v.patientId ||
      v.consultationId ||
      v._id ||
      v.pgId ||
      null;
    return id ? String(id).trim() : null;
  }

  const s = String(v).trim();
  return s.length ? s : null;
};

const resolvePatientId = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = normalizeId(candidate);
    if (normalized) return normalized;

    if (candidate && typeof candidate === "object") {
      const nestedPatient = candidate.patient || candidate.user || candidate.patientData || null;
      const nestedId = normalizeId(nestedPatient);
      if (nestedId) return nestedId;
    }
  }

  return null;
};

// ✅ IMPORTANT: export nommé
export function usePrescription() {
  const dispatch = useDispatch();

  const doctorState = useSelector((state) => state.doctor || {});
  const currentDoctor = doctorState.user || null;

  const auth = useSelector((state) => state.auth || {});
  const authUser = auth.user || null;

  const doctorId = useMemo(() => {
    return (
      normalizeId(currentDoctor?.id) ||
      normalizeId(currentDoctor?.pgId) ||
      normalizeId(currentDoctor?.doctorId) ||
      normalizeId(currentDoctor?._id) ||
      normalizeId(authUser?.id) ||
      normalizeId(authUser?.pgId) ||
      normalizeId(authUser?.doctorId) ||
      normalizeId(authUser?._id) ||
      null
    );
  }, [currentDoctor, authUser]);

  useEffect(() => {
    if (!currentDoctor && authUser) dispatch(getCurrentDoctor());
  }, [currentDoctor, authUser, dispatch]);

  const createPrescriptionForPatient = useCallback(
    async ({
      patientId,
      consultationId,
      description,
      documentType,
      typeOrdonnance,
      fullName,
      fullNameAr,
      specialty,
      specialtyAr,
      phone,
      email,
      address,
      addressAr,
      couleurTexte,
      couleurLogo,
      logoUrl,
    }) => {
      const safeDoctorId = normalizeId(doctorId);
      const safePatientId = resolvePatientId(
        patientId,
        currentDoctor?.patientId,
        currentDoctor?.userId,
        authUser?.patientId,
        authUser?.userId,
        authUser?.id,
        currentDoctor?.id
      );
      const safeConsultationId = normalizeId(consultationId);

      if (!safeDoctorId) throw new Error("Doctor ID manquant");
      if (!safePatientId) throw new Error("Patient ID manquant.");

      const payload = {
        doctorId: safeDoctorId,
        patientId: safePatientId,
        ...(safeConsultationId ? { consultationId: safeConsultationId } : {}),
        description: pick(description, ""),
        documentType: pick(documentType, undefined),
        type: pick(typeOrdonnance, DOCUMENT_TYPE_TO_ORDONNANCE[documentType] ?? "autre"),

        fullName: pick(fullName, currentDoctor?.fullName ?? ""),
        fullNameAr: pick(fullNameAr, currentDoctor?.fullNameAr ?? ""),
        specialty: pick(specialty, currentDoctor?.specialty ?? ""),
        specialtyAr: pick(specialtyAr, currentDoctor?.specialtyAr ?? ""),
        phone: pick(phone, currentDoctor?.phone ?? ""),
        email: pick(email, currentDoctor?.email ?? ""),
        address: pick(address, currentDoctor?.clinicAddress ?? ""),
        addressAr: pick(addressAr, currentDoctor?.clinicAddressAr ?? ""),

        couleurTexte: pick(couleurTexte, undefined),
        couleurLogo: pick(couleurLogo, undefined),
        logoUrl: pick(logoUrl, undefined),
      };

      const action = await dispatch(createPrescription(payload));
      if (createPrescription.rejected.match(action)) {
        throw new Error(
          action.payload ||
            action.error?.message ||
            "Erreur lors de la création de l'ordonnance"
        );
      }

      return action.payload;
    },
    [dispatch, doctorId, currentDoctor]
  );

  const updatePrescriptionForPatient = useCallback(
    async ({ prescriptionId, data }) => {
      const safeDoctorId = normalizeId(doctorId);
      const safePrescriptionId = normalizeId(prescriptionId);

      if (!safeDoctorId) throw new Error("Doctor ID manquant");
      if (!safePrescriptionId) throw new Error("Prescription ID manquant");

      const action = await dispatch(
        updatePrescription({
          doctorId: safeDoctorId,
          prescriptionId: safePrescriptionId,
          data,
        })
      );

      if (updatePrescription.rejected.match(action)) {
        throw new Error(
          action.payload ||
            action.error?.message ||
            "Erreur lors de la mise a jour du document"
        );
      }

      return action.payload;
    },
    [dispatch, doctorId]
  );

  const classifyAndSaveDocumentsForPatient = useCallback(
    async ({
      patientId,
      consultationId,
      text,
      source = "manual",
      language = "fr",
      fullName,
      fullNameAr,
      specialty,
      specialtyAr,
      phone,
      email,
      address,
      addressAr,
      couleurTexte,
      couleurLogo,
      logoUrl,
    }) => {
      const safeDoctorId = normalizeId(doctorId);
      const safePatientId = resolvePatientId(
        patientId,
        currentDoctor?.patientId,
        currentDoctor?.userId,
        authUser?.patientId,
        authUser?.userId,
        authUser?.id,
        currentDoctor?.id
      );
      const safeConsultationId = normalizeId(consultationId);
      const safeText = String(text || "").trim();

      if (!safeDoctorId) throw new Error("Doctor ID manquant");
      if (!safePatientId) throw new Error("Patient ID manquant.");
      if (!safeText) throw new Error("Texte medical vide");

      const payload = {
        doctorId: safeDoctorId,
        patientId: safePatientId,
        text: safeText,
        source,
        language,
        ...(safeConsultationId ? { consultationId: safeConsultationId } : {}),

        fullName: pick(fullName, currentDoctor?.fullName ?? ""),
        fullNameAr: pick(fullNameAr, currentDoctor?.fullNameAr ?? ""),
        specialty: pick(specialty, currentDoctor?.specialty ?? ""),
        specialtyAr: pick(specialtyAr, currentDoctor?.specialtyAr ?? ""),
        phone: pick(phone, currentDoctor?.phone ?? ""),
        email: pick(email, currentDoctor?.email ?? ""),
        address: pick(address, currentDoctor?.clinicAddress ?? ""),
        addressAr: pick(addressAr, currentDoctor?.clinicAddressAr ?? ""),

        couleurTexte: pick(couleurTexte, undefined),
        couleurLogo: pick(couleurLogo, undefined),
        logoUrl: pick(logoUrl, undefined),
      };

      const action = await dispatch(classifyAndSavePrescriptionDocuments(payload));
      if (classifyAndSavePrescriptionDocuments.rejected.match(action)) {
        throw new Error(
          action.payload ||
            action.error?.message ||
            "Erreur lors de la classification et sauvegarde"
        );
      }

      return action.payload;
    },
    [dispatch, doctorId, currentDoctor]
  );

  const loadPrescriptionsForPatientAsDoctor = useCallback(
    async ({ patientId }) => {
      const safeDoctorId = normalizeId(doctorId);
      const safePatientId = normalizeId(patientId);

      if (!safeDoctorId) throw new Error("Doctor ID manquant");
      if (!safePatientId) throw new Error("Patient ID manquant");

      const action = await dispatch(
        fetchPrescriptionsByPatientForDoctor({
          doctorId: safeDoctorId,
          patientId: safePatientId,
        })
      );

      if (fetchPrescriptionsByPatientForDoctor.rejected.match(action)) {
        throw new Error(action.payload || action.error?.message || "Erreur chargement");
      }

      return action.payload;
    },
    [dispatch, doctorId]
  );

  const getPrescriptionForConsultation = useCallback(
    async ({ consultationId }) => {
      const safeDoctorId = normalizeId(doctorId);
      const safeConsultationId = normalizeId(consultationId);

      if (!safeDoctorId) throw new Error("Doctor ID manquant");
      if (!safeConsultationId) throw new Error("consultationId manquant");

      const action = await dispatch(
        fetchPrescriptionByConsultation({
          doctorId: safeDoctorId,
          consultationId: safeConsultationId,
        })
      );

      if (fetchPrescriptionByConsultation.rejected.match(action)) {
        throw new Error(action.payload || action.error?.message || "Erreur chargement");
      }

      return action.payload;
    },
    [dispatch, doctorId]
  );

  return {
    doctorId,
    currentDoctor,
    createPrescriptionForPatient,
    updatePrescriptionForPatient,
    classifyAndSaveDocumentsForPatient,
    loadPrescriptionsForPatientAsDoctor,
    getPrescriptionForConsultation,
  };
}
